import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getExamStatsSummary } from '../lib/examStats';
import { getVisitorStats, getPageViewStats, getVisitorLogs, recalcVisitorStatsFromLogs, trackVisitorForce, seedVisitorCounter, trackPageView } from '../lib/visitorTracker';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { getFirestoreUsers, getFirestorePlans, updateFirestoreSubscription, type FirestoreUser, type FirestorePlan } from '../lib/adminFirestore';

const ADMIN_EMAILS = ['ashrafede@gmail.com', 'ashrafede1@gmail.com'];
const ADMIN_DISPLAY_NAMES = ['Ashraf EL desoky'];
const USERS_REGISTRY_KEY = 'pmp_admin_users_registry';
const PLANS_REGISTRY_KEY = 'pmp_admin_plans';

const DEFAULT_PLANS: LocalPlan[] = [
  { id: 1, name: 'Trial', name_ar: 'تجريبي', duration_days: 10, price_usd: 0, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 0 },
  { id: 2, name: 'Basic', name_ar: 'أساسي', duration_days: 0, price_usd: 0, question_limit: 20, has_full_access: 0, has_analytics: 0, has_priority_support: 0 },
  { id: 3, name: 'Pro', name_ar: 'احترافي', duration_days: 30, price_usd: 19.99, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 0 },
  { id: 4, name: 'Enterprise', name_ar: 'مؤسسي', duration_days: 365, price_usd: 199.99, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 1 },
];

interface LocalUser {
  id: number;
  email: string;
  display_name?: string;
  firebase_uid?: string;
  preferred_language?: string;
  registered_at?: string;
  plan_name?: string;
  status?: string;
}

interface LocalPlan {
  id: number;
  name: string;
  name_ar?: string;
  duration_days: number;
  price_usd: number;
  question_limit?: number | null;
  has_full_access: number;
  has_analytics: number;
  has_priority_support: number;
}

interface Question {
  id: number;
  question_id: string;
  type: string;
  cognitive_type?: string;
  scenario_en: string;
  scenario_ar: string;
  options_en: string[];
  options_ar: string[];
  domain: string;
  difficulty: string;
  correct_answer: string;
  explanation_en: string;
  explanation_ar: string;
  choice_explanations_en: string[];
  choice_explanations_ar: string[];
  reference?: string;
  firestoreId?: string;
}

function loadUsers(): LocalUser[] {
  try {
    const raw = localStorage.getItem(USERS_REGISTRY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveUsers(users: LocalUser[]) {
  localStorage.setItem(USERS_REGISTRY_KEY, JSON.stringify(users));
}

function loadPlans(): LocalPlan[] {
  try {
    const raw = localStorage.getItem(PLANS_REGISTRY_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const plans = DEFAULT_PLANS;
  localStorage.setItem(PLANS_REGISTRY_KEY, JSON.stringify(plans));
  return plans;
}

function savePlans(plans: LocalPlan[]) {
  localStorage.setItem(PLANS_REGISTRY_KEY, JSON.stringify(plans));
}

function collectAllUsers(): LocalUser[] {
  const registered = loadUsers();
  const knownIds = new Set(registered.map((u) => u.id));

  // Scan localStorage for all exam session keys and extract user IDs
  const discovered: LocalUser[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('pmp_exam_stats_v1_') || key.endsWith('anonymous')) continue;
    const idStr = key.replace('pmp_exam_stats_v1_', '');
    const id = Number(idStr);
    if (!isNaN(id) && !knownIds.has(id)) {
      discovered.push({ id, email: `user_${id}@unknown`, plan_name: 'Trial', status: 'trial' });
    }
  }

  return [...registered, ...discovered];
}

export default function AdminPage() {
  const { t, i18n } = useTranslation();
  const { isAdmin, user } = useAuthStore();
  const isRTL = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState<'users' | 'plans' | 'questions'>('users');
  const [users, setUsers] = useState<LocalUser[]>([]);
  const [plans, setPlans] = useState<LocalPlan[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<LocalUser | null>(null);
  const [editingPlan, setEditingPlan] = useState<Partial<LocalPlan> | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Partial<Question> | null>(null);
  const [message, setMessage] = useState('');
  const [visitorStats, setVisitorStats] = useState({ total: 0, today: 0 });
  const [pageViewStats, setPageViewStats] = useState({ total: 0, today: 0 });
  const [seedTotal, setSeedTotal] = useState('');
  const [seedToday, setSeedToday] = useState('');
  const [firestoreUsers, setFirestoreUsers] = useState<FirestoreUser[]>([]);
  const [firestorePlans, setFirestorePlans] = useState<FirestorePlan[]>([]);
  const [visitorLogs, setVisitorLogs] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const hasAdminAccess =
    isAdmin ||
    user?.email.includes('admin') ||
    ADMIN_EMAILS.includes(user?.email || '') ||
    ADMIN_DISPLAY_NAMES.includes(user?.display_name || '');

  const loadData = async () => {
    setLoadingUsers(true);
    try {
      const [fireUsers, firePlans] = await Promise.all([getFirestoreUsers(), getFirestorePlans()]);
      setFirestoreUsers(fireUsers);
      setFirestorePlans(firePlans);

      // Map Firestore users to the LocalUser shape used by the UI
      const mappedUsers: LocalUser[] = fireUsers.map((u) => ({
        id: u.id,
        email: u.email,
        display_name: u.display_name || undefined,
        firebase_uid: u.uid,
        preferred_language: u.preferred_language,
        registered_at: u.created_at,
        plan_name: u.plan_name || 'Trial',
        status: u.status || 'trial',
      }));

      // Merge with localStorage registry as fallback
      const localUsers = collectAllUsers();
      const localOnly = localUsers.filter((lu) => !mappedUsers.find((mu) => mu.id === lu.id));
      setUsers([...mappedUsers, ...localOnly]);
      setPlans(firePlans.map((p) => ({ ...p, name_ar: p.name_ar || p.name })));
    } catch (err) {
      console.error('Failed to load Firestore admin data:', err);
      setUsers(collectAllUsers());
      setPlans(loadPlans());
    } finally {
      setLoadingUsers(false);
    }
    loadQuestions();
  };

  const loadQuestions = async () => {
    try {
      const q = query(collection(db, 'questions'), orderBy('question_id'));
      const querySnapshot = await getDocs(q);
      const questionsData: Question[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Handle options that might be stored as strings (pipe-separated) or arrays
        const optionsEn = Array.isArray(data.options_en) ? data.options_en : (data.options_en || '').split('|').filter((o: string) => o.trim());
        const optionsAr = Array.isArray(data.options_ar) ? data.options_ar : (data.options_ar || '').split('|').filter((o: string) => o.trim());
        const choiceExplEn = Array.isArray(data.choice_explanations_en) ? data.choice_explanations_en : JSON.parse(data.choice_explanations_en || '[]');
        const choiceExplAr = Array.isArray(data.choice_explanations_ar) ? data.choice_explanations_ar : JSON.parse(data.choice_explanations_ar || '[]');
        
        return {
          id: data.id,
          question_id: data.question_id,
          type: data.type,
          cognitive_type: data.cognitive_type,
          scenario_en: data.scenario_en,
          scenario_ar: data.scenario_ar,
          options_en: optionsEn,
          options_ar: optionsAr,
          domain: data.domain,
          difficulty: data.difficulty,
          reference: data.reference || 'PMBOK 08',
          correct_answer: data.correct_answer,
          explanation_en: data.explanation_en,
          explanation_ar: data.explanation_ar,
          choice_explanations_en: choiceExplEn,
          choice_explanations_ar: choiceExplAr,
          firestoreId: doc.id
        };
      });
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to load questions:', error);
    }
  };

  useEffect(() => {
    if (!hasAdminAccess) return;
    loadData();
    getVisitorStats().then(setVisitorStats);
    getPageViewStats().then(setPageViewStats);
    getVisitorLogs(100).then(setVisitorLogs);
  }, [hasAdminAccess]);

  const totalAttempts = users.reduce((sum, u) => {
    const stats = getExamStatsSummary(u.id);
    return sum + stats.totalQuestions;
  }, 0);

  const updateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    const all = loadUsers();
    const idx = all.findIndex((u) => u.id === editingUser.id);
    if (idx >= 0) all[idx] = editingUser;
    else all.unshift(editingUser);
    saveUsers(all);
    setEditingUser(null);
    setMessage('User updated');
    loadData();
  };

  const setPlan = async (userId: number, planName: string) => {
    const fireUser = firestoreUsers.find((u) => u.id === userId);
    const firePlan = firestorePlans.find((p) => p.name === planName);
    if (fireUser && firePlan) {
      try {
        await updateFirestoreSubscription(fireUser.uid, firePlan, 'active');
      } catch (err) {
        console.error('Failed to update Firestore subscription:', err);
      }
    }

    const all = loadUsers();
    const idx = all.findIndex((u) => u.id === userId);
    if (idx >= 0) {
      all[idx].plan_name = planName;
      all[idx].status = 'active';
    }
    saveUsers(all);
    setMessage(`Plan set to ${planName}`);
    loadData();
  };

  const deleteUser = (userId: number) => {
    if (!confirm('Delete this user and all their data?')) return;
    const all = loadUsers().filter((u) => u.id !== userId);
    saveUsers(all);
    localStorage.removeItem(`pmp_exam_stats_v1_${userId}`);
    setMessage('User deleted');
    loadData();
  };

  const savePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan?.name) return;
    const all = loadPlans();
    const planId = editingPlan.id || Math.max(0, ...all.map((p) => p.id)) + 1;
    const planData: LocalPlan = { ...editingPlan, id: planId } as LocalPlan;
    if (editingPlan.id) {
      const idx = all.findIndex((p) => p.id === editingPlan.id);
      if (idx >= 0) all[idx] = planData;
    } else {
      all.push(planData);
    }
    savePlans(all);

    // Sync to Firestore plans collection
    try {
      await setDoc(doc(db, 'plans', String(planId)), {
        id: planId,
        name: planData.name,
        name_ar: planData.name_ar || planData.name,
        duration_days: planData.duration_days,
        price_usd: planData.price_usd,
        question_limit: planData.question_limit ?? null,
        has_full_access: planData.has_full_access,
        has_analytics: planData.has_analytics,
        has_priority_support: planData.has_priority_support,
      });
    } catch (err) {
      console.error('Failed to sync plan to Firestore:', err);
    }

    setEditingPlan(null);
    setMessage('Plan saved');
    loadData();
  };

  const deletePlan = (planId: number) => {
    if (!confirm('Delete this plan?')) return;
    savePlans(loadPlans().filter((p) => p.id !== planId));
    setMessage('Plan deleted');
    loadData();
  };

  const saveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQuestion) return;
    try {
      if (editingQuestion.firestoreId) {
        // Update existing question
        const questionRef = doc(db, 'questions', editingQuestion.firestoreId);
        await updateDoc(questionRef, {
          question_id: editingQuestion.question_id,
          type: editingQuestion.type,
          cognitive_type: editingQuestion.cognitive_type,
          scenario_en: editingQuestion.scenario_en,
          scenario_ar: editingQuestion.scenario_ar,
          options_en: editingQuestion.options_en,
          options_ar: editingQuestion.options_ar,
          domain: editingQuestion.domain,
          difficulty: editingQuestion.difficulty,
          reference: editingQuestion.reference || 'PMBOK 08',
          correct_answer: editingQuestion.correct_answer,
          explanation_en: editingQuestion.explanation_en,
          explanation_ar: editingQuestion.explanation_ar,
          choice_explanations_en: editingQuestion.choice_explanations_en,
          choice_explanations_ar: editingQuestion.choice_explanations_ar,
        });
        setMessage('Question updated');
      } else {
        // Create new question
        await addDoc(collection(db, 'questions'), {
          id: Date.now(),
          question_id: editingQuestion.question_id,
          type: editingQuestion.type,
          cognitive_type: editingQuestion.cognitive_type,
          scenario_en: editingQuestion.scenario_en,
          scenario_ar: editingQuestion.scenario_ar,
          options_en: editingQuestion.options_en,
          options_ar: editingQuestion.options_ar,
          domain: editingQuestion.domain,
          difficulty: editingQuestion.difficulty,
          reference: editingQuestion.reference || 'PMBOK 08',
          correct_answer: editingQuestion.correct_answer,
          explanation_en: editingQuestion.explanation_en,
          explanation_ar: editingQuestion.explanation_ar,
          choice_explanations_en: editingQuestion.choice_explanations_en,
          choice_explanations_ar: editingQuestion.choice_explanations_ar,
          created_at: new Date().toISOString(),
        });
        setMessage('Question created');
      }
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      console.error('Failed to save question:', error);
      setMessage('Failed to save question');
    }
  };

  const deleteQuestion = async (firestoreId: string) => {
    if (!confirm('Delete this question?')) return;
    try {
      await deleteDoc(doc(db, 'questions', firestoreId));
      setMessage('Question deleted');
      loadQuestions();
    } catch (error) {
      console.error('Failed to delete question:', error);
      setMessage('Failed to delete question');
    }
  };

  const filteredUsers = users.filter((u) =>
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.display_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const filteredQuestions = questions.filter((q) =>
    (q.question_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (q.domain || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!hasAdminAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-destructive mb-4">Access Denied</h1>
        <p className="text-muted-foreground">You do not have permission to access the admin dashboard.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-2">{t('admin.title') || 'Admin Dashboard'}</h1>
      <p className="text-xs text-muted-foreground mb-6">Connected to Firebase — users, plans, subscriptions and visitors stored in Firestore</p>

      {message && (
        <div className="mb-4 p-3 text-sm bg-green-100 text-green-700 rounded-lg flex justify-between">
          {message}
          <button onClick={() => setMessage('')} className="text-green-900 font-bold">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <div className="p-4 border rounded-lg bg-card">
          <div className="text-2xl font-bold">{users.length}</div>
          <div className="text-sm text-muted-foreground">{t('admin.totalUsers') || 'Total Users'}</div>
        </div>
        <div className="p-4 border rounded-lg bg-card">
          <div className="text-2xl font-bold">{users.filter((u) => u.status === 'trial' || u.status === 'active').length}</div>
          <div className="text-sm text-muted-foreground">{t('admin.activeSubs') || 'Active Subscriptions'}</div>
        </div>
        <div className="p-4 border rounded-lg bg-card">
          <div className="text-2xl font-bold">{totalAttempts}</div>
          <div className="text-sm text-muted-foreground">{t('admin.totalAttempts') || 'Total Attempts'}</div>
        </div>
        <div className="p-4 border rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{visitorStats.total.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">{isRTL ? 'إجمالي الزوار' : 'Total Visitors'}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={async () => {
                try {
                  await trackVisitorForce();
                  const stats = await getVisitorStats();
                  setVisitorStats(stats);
                  setMessage(isRTL ? 'تم تسجيل زيارة اختبارية' : 'Test visit tracked');
                } catch (err: any) {
                  setMessage(err.message || 'Failed to track test visit');
                }
              }}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:opacity-90"
            >
              {isRTL ? 'زيارة اختبارية' : 'Test Visit'}
            </button>
            <button
              onClick={async () => {
                const stats = await getVisitorStats();
                setVisitorStats(stats);
              }}
              className="px-2 py-1 text-xs border rounded hover:bg-accent"
            >
              {isRTL ? 'تحديث' : 'Refresh'}
            </button>
            <button
              onClick={async () => {
                const stats = await recalcVisitorStatsFromLogs();
                setVisitorStats(stats);
                setMessage(isRTL ? 'تم إعادة حساب العداد من السجلات' : 'Counter recalculated from logs');
              }}
              className="px-2 py-1 text-xs border rounded hover:bg-accent"
            >
              {isRTL ? 'إعادة حساب' : 'Recalc'}
            </button>
          </div>
        </div>
        <div className="p-4 border rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{visitorStats.today.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">{isRTL ? 'زوار اليوم' : 'Visitors Today'}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <input
              type="number"
              placeholder={isRTL ? 'إجمالي' : 'Total'}
              value={seedTotal}
              onChange={(e) => setSeedTotal(e.target.value)}
              className="w-16 px-2 py-1 text-xs border rounded bg-background"
            />
            <input
              type="number"
              placeholder={isRTL ? 'اليوم' : 'Today'}
              value={seedToday}
              onChange={(e) => setSeedToday(e.target.value)}
              className="w-16 px-2 py-1 text-xs border rounded bg-background"
            />
            <button
              onClick={async () => {
                const total = Number(seedTotal);
                const today = Number(seedToday);
                if (!isNaN(total) && !isNaN(today)) {
                  await seedVisitorCounter(total, today);
                  const stats = await getVisitorStats();
                  setVisitorStats(stats);
                  setMessage(isRTL ? 'تم تحديث العداد' : 'Counter updated');
                  setSeedTotal('');
                  setSeedToday('');
                }
              }}
              className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:opacity-90"
            >
              {isRTL ? 'تعيين' : 'Set'}
            </button>
          </div>
        </div>
        <div className="p-4 border rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-500/5">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{pageViewStats.total.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">{isRTL ? 'إجمالي مشاهدات الصفحة' : 'Total Page Views'}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {isRTL ? 'اليوم: ' : 'Today: '}{pageViewStats.today.toLocaleString()}
          </div>
          <button
            onClick={async () => {
              await trackPageView();
              const stats = await getPageViewStats();
              setPageViewStats(stats);
              setMessage(isRTL ? 'تم تسجيل مشاهدة صفحة' : 'Page view tracked');
            }}
            className="px-2 py-1 mt-3 text-xs bg-purple-600 text-white rounded hover:opacity-90"
          >
            {isRTL ? 'تسجيل مشاهدة' : 'Track View'}
          </button>
        </div>
      </div>

      {/* Visitor Logs */}
      <div className="mb-8 p-6 border rounded-lg bg-card">
        <h2 className="text-lg font-semibold mb-4">{isRTL ? 'سجل الزوار' : 'Visitor Logs'}</h2>
        <p className="text-xs text-muted-foreground mb-4">
          {isRTL ? 'آخر 100 زيارة مع التاريخ والوقت من قاعدة بيانات Firebase' : 'Last 100 visits with date/time from Firebase database'}
        </p>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left p-2">{isRTL ? 'الوقت' : 'Time'}</th>
                <th className="text-left p-2">{isRTL ? 'النوع' : 'Type'}</th>
                <th className="text-left p-2">{isRTL ? 'المسار' : 'Path'}</th>
                <th className="text-left p-2">{isRTL ? 'المستخدم' : 'User'}</th>
              </tr>
            </thead>
            <tbody>
              {visitorLogs.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-muted-foreground">
                    {isRTL ? 'لا توجد سجلات زوار بعد' : 'No visitor logs yet'}
                  </td>
                </tr>
              )}
              {visitorLogs.map((log) => {
                const ts = log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : log.timestamp || '-';
                return (
                  <tr key={log.id} className="border-b hover:bg-muted/30">
                    <td className="p-2 whitespace-nowrap">{ts}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${log.type === 'visitor' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {log.type || 'pageView'}
                      </span>
                    </td>
                    <td className="p-2 text-muted-foreground">{log.path || '/'}</td>
                    <td className="p-2">{log.email || log.uid || (isRTL ? 'زائر' : 'Guest')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {(['users', 'plans', 'questions'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'users' ? (t('admin.users') || 'Users') : tab === 'plans' ? (t('admin.plans') || 'Plans') : 'Questions'}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="p-6 border rounded-lg bg-card">
          <div className="flex gap-4 mb-4 items-center">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              className="px-3 py-2 border rounded-lg bg-background flex-1"
            />
            <button onClick={loadData} className="px-4 py-2 border rounded-lg hover:bg-accent">Refresh</button>
            {loadingUsers && <span className="text-xs text-muted-foreground">{isRTL ? 'جاري التحميل...' : 'Loading...'}</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Sessions</th>
                  <th className="text-left p-2">Accuracy</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={7}>
                      No users found. Users appear here when they register or take an exam.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const stats = getExamStatsSummary(u.id);
                    return (
                      <tr key={u.id} className="border-b hover:bg-muted/20">
                        <td className="p-2">{u.email}</td>
                        <td className="p-2">{u.display_name || '-'}</td>
                        <td className="p-2">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">{u.plan_name || 'Trial'}</span>
                        </td>
                        <td className="p-2">{u.status || 'trial'}</td>
                        <td className="p-2">{stats.totalSessions}</td>
                        <td className="p-2">{stats.accuracy}%</td>
                        <td className="p-2">
                          <div className="flex gap-2 items-center">
                            <button onClick={() => setEditingUser(u)} className="text-primary hover:underline text-xs">Edit</button>
                            <select
                              value=""
                              onChange={(e) => { if (e.target.value) setPlan(u.id, e.target.value); }}
                              className="text-xs border rounded px-1 py-0.5 bg-background"
                            >
                              <option value="">Set plan</option>
                              {plans.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                            </select>
                            <button onClick={() => deleteUser(u.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="p-6 border rounded-lg bg-card">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setEditingPlan({ name: '', duration_days: 30, price_usd: 0, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 0 })}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
            >
              Create Plan
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left p-2">Plan</th>
                <th className="text-left p-2">Price (USD)</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2">Question Limit</th>
                <th className="text-left p-2">Features</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className="border-b hover:bg-muted/20">
                  <td className="p-2 font-medium">{plan.name}</td>
                  <td className="p-2">{plan.price_usd === 0 ? 'Free' : `$${plan.price_usd}`}</td>
                  <td className="p-2">{plan.duration_days} days</td>
                  <td className="p-2">{plan.question_limit == null ? 'Unlimited' : plan.question_limit}</td>
                  <td className="p-2 text-xs text-muted-foreground">
                    {[plan.has_full_access && 'Full', plan.has_analytics && 'Analytics', plan.has_priority_support && 'Priority'].filter(Boolean).join(', ') || 'Basic'}
                  </td>
                  <td className="p-2 flex gap-2">
                    <button onClick={() => setEditingPlan({ ...plan })} className="text-primary hover:underline text-xs">Edit</button>
                    <button onClick={() => deletePlan(plan.id)} className="text-red-600 hover:underline text-xs">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="p-6 border rounded-lg bg-card">
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by question ID or domain..."
              className="px-3 py-2 border rounded-lg bg-background flex-1"
            />
            <span className="px-3 py-2 text-sm text-muted-foreground flex items-center">
              {filteredQuestions.length} / {questions.length}
            </span>
            <button onClick={loadQuestions} className="px-4 py-2 border rounded-lg hover:bg-accent">Refresh</button>
            <button
              onClick={() => setEditingQuestion({
                question_id: '',
                type: 'single',
                cognitive_type: 'scenario',
                scenario_en: '',
                scenario_ar: '',
                options_en: ['', '', '', ''],
                options_ar: ['', '', '', ''],
                domain: 'Process',
                difficulty: 'Hard',
                correct_answer: '',
                explanation_en: '',
                explanation_ar: '',
                choice_explanations_en: ['', '', '', ''],
                choice_explanations_ar: ['', '', '', ''],
              })}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
            >
              Add Question
            </button>
            <button
              onClick={async () => {
                if (!confirm('Set reference=PMBOK 08 for all questions?')) return;
                let updated = 0;
                for (const q of questions) {
                  if (q.firestoreId && q.reference !== 'PMBOK 08') {
                    try {
                      await updateDoc(doc(db, 'questions', q.firestoreId), { reference: 'PMBOK 08' });
                      updated++;
                    } catch (err) {
                      console.error(`Failed to update ${q.question_id}:`, err);
                    }
                  }
                }
                setMessage(`Updated ${updated} questions to PMBOK 08`);
                loadQuestions();
              }}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:opacity-90"
            >
              Set All to PMBOK 08
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">Question ID</th>
                  <th className="text-left p-2">Domain</th>
                  <th className="text-left p-2">Difficulty</th>
                  <th className="text-left p-2">Reference</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuestions.length === 0 ? (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={7}>
                      No questions found.
                    </td>
                  </tr>
                ) : (
                  filteredQuestions.map((q) => (
                    <tr key={q.id} className="border-b hover:bg-muted/20">
                      <td className="p-2">{q.id}</td>
                      <td className="p-2">{q.question_id}</td>
                      <td className="p-2">{q.domain}</td>
                      <td className="p-2">{q.difficulty}</td>
                      <td className="p-2">{q.reference || 'PMBOK 08'}</td>
                      <td className="p-2">{q.type}</td>
                      <td className="p-2 flex gap-2">
                        <button onClick={() => setEditingQuestion({ ...q })} className="text-primary hover:underline text-xs">Edit</button>
                        <button onClick={() => deleteQuestion(q.firestoreId!)} className="text-red-600 hover:underline text-xs">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4">Edit User</h3>
            <form onSubmit={updateUser} className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Email</label>
                <input type="email" value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Display Name</label>
                <input type="text" value={editingUser.display_name || ''} onChange={(e) => setEditingUser({ ...editingUser, display_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Plan</label>
                <select value={editingUser.plan_name || 'Trial'} onChange={(e) => setEditingUser({ ...editingUser, plan_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                  {plans.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Status</label>
                <select value={editingUser.status || 'trial'} onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 border rounded-lg hover:bg-accent">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {editingPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-4">{editingPlan.id ? 'Edit Plan' : 'Create Plan'}</h3>
            <form onSubmit={savePlan} className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Name</label>
                <input type="text" value={editingPlan.name || ''} onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Arabic Name</label>
                <input type="text" value={editingPlan.name_ar || ''} onChange={(e) => setEditingPlan({ ...editingPlan, name_ar: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Duration (days)</label>
                  <input type="number" value={editingPlan.duration_days || ''} onChange={(e) => setEditingPlan({ ...editingPlan, duration_days: Number(e.target.value) })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Price (USD)</label>
                  <input type="number" step="0.01" value={editingPlan.price_usd ?? ''} onChange={(e) => setEditingPlan({ ...editingPlan, price_usd: Number(e.target.value) })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Question Limit (empty = unlimited)</label>
                <input type="number" value={editingPlan.question_limit ?? ''} onChange={(e) => setEditingPlan({ ...editingPlan, question_limit: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-2 border rounded-lg bg-background" />
              </div>
              <div className="flex gap-4 text-sm">
                {(['has_full_access', 'has_analytics', 'has_priority_support'] as const).map((key) => (
                  <label key={key} className="flex items-center gap-1">
                    <input type="checkbox" checked={!!editingPlan[key]} onChange={(e) => setEditingPlan({ ...editingPlan, [key]: e.target.checked ? 1 : 0 })} />
                    {key === 'has_full_access' ? 'Full Access' : key === 'has_analytics' ? 'Analytics' : 'Priority Support'}
                  </label>
                ))}
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setEditingPlan(null)} className="px-4 py-2 border rounded-lg hover:bg-accent">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-card border rounded-lg p-6 w-full max-w-4xl shadow-xl my-8">
            <h3 className="text-lg font-bold mb-4">{editingQuestion.id ? 'Edit Question' : 'Add Question'}</h3>
            <form onSubmit={saveQuestion} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Question ID</label>
                  <input type="text" value={editingQuestion.question_id || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, question_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Type</label>
                  <select value={editingQuestion.type || 'single'} onChange={(e) => setEditingQuestion({ ...editingQuestion, type: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                    <option value="single">Single Choice</option>
                    <option value="multiple">Multiple Choice</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Cognitive Type</label>
                  <select value={editingQuestion.cognitive_type || 'scenario'} onChange={(e) => setEditingQuestion({ ...editingQuestion, cognitive_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                    <option value="scenario">Scenario</option>
                    <option value="knowledge">Knowledge</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Domain</label>
                  <select value={editingQuestion.domain || 'Process'} onChange={(e) => setEditingQuestion({ ...editingQuestion, domain: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                    <option value="People">People</option>
                    <option value="Process">Process</option>
                    <option value="Business Environment">Business Environment</option>
                    <option value="Delivery">Delivery</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Difficulty</label>
                  <select value={editingQuestion.difficulty || 'Hard'} onChange={(e) => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                    <option value="Hard">Hard</option>
                    <option value="Expert">Expert</option>
                    <option value="Hyper-Expert">Hyper-Expert</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Reference</label>
                  <select value={editingQuestion.reference || 'PMBOK 08'} onChange={(e) => setEditingQuestion({ ...editingQuestion, reference: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background">
                    <option value="PMBOK 08">PMBOK 08</option>
                    <option value="PMBOK 07">PMBOK 07</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Scenario (English)</label>
                <textarea value={editingQuestion.scenario_en || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, scenario_en: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background h-32" required />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Scenario (Arabic)</label>
                <textarea value={editingQuestion.scenario_ar || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, scenario_ar: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background h-32" dir="rtl" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Options (English) - one per line</label>
                <textarea
                  value={(editingQuestion.options_en || []).join('\n')}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, options_en: e.target.value.split('\n').filter(o => o.trim()) })}
                  className="w-full px-3 py-2 border rounded-lg bg-background h-32"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Options (Arabic) - one per line</label>
                <textarea
                  value={(editingQuestion.options_ar || []).join('\n')}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, options_ar: e.target.value.split('\n').filter(o => o.trim()) })}
                  className="w-full px-3 py-2 border rounded-lg bg-background h-32"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Correct Answer (exact text from English options)</label>
                <input type="text" value={editingQuestion.correct_answer || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, correct_answer: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background" required />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Explanation (English)</label>
                <textarea value={editingQuestion.explanation_en || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation_en: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background h-32" required />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Explanation (Arabic)</label>
                <textarea value={editingQuestion.explanation_ar || ''} onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation_ar: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-background h-32" dir="rtl" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Choice Explanations (English) - one per line (in order of options)</label>
                <textarea
                  value={(editingQuestion.choice_explanations_en || []).join('\n')}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, choice_explanations_en: e.target.value.split('\n').filter(o => o.trim()) })}
                  className="w-full px-3 py-2 border rounded-lg bg-background h-32"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Choice Explanations (Arabic) - one per line (in order of options)</label>
                <textarea
                  value={(editingQuestion.choice_explanations_ar || []).join('\n')}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, choice_explanations_ar: e.target.value.split('\n').filter(o => o.trim()) })}
                  className="w-full px-3 py-2 border rounded-lg bg-background h-32"
                  dir="rtl"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setEditingQuestion(null)} className="px-4 py-2 border rounded-lg hover:bg-accent">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
